import {
  KreFormExtension,
  KreFormConfig,
  TemplateManipulators,
} from "../../services/kre-form.config";
import {
  KreFormFieldConfigCache,
  KreFormFieldConfig,
} from "../../components/kre-form.field.config";
import { FormGroup } from "@angular/forms";
import {
  getFieldId,
  isUndefined,
  getFieldValue,
  reverseDeepMerge,
  assignFieldValue,
} from "../../utils";
import { Guid } from "../../guid";

export class CoreExtension implements KreFormExtension {
  private formId = 0;
  constructor(private formlyConfig: KreFormConfig) {}

  prePopulate(field: KreFormFieldConfigCache) {
    this.getFieldComponentInstance(field).prePopulate();
    if (field.parent) {
      return;
    }

    const fieldTransforms =
      (field.options && field.options.fieldTransform) ||
      this.formlyConfig.extras.fieldTransform;
    (Array.isArray(fieldTransforms)
      ? fieldTransforms
      : [fieldTransforms]
    ).forEach((fieldTransform) => {
      if (fieldTransform) {
        const fieldGroup = fieldTransform(
          field.fieldGroup,
          field.model,
          <FormGroup>field.formControl,
          field.options
        );
        if (!fieldGroup) {
          throw new Error("fieldTransform must return an array of fields");
        }
      }
    });
  }

  onPopulate(field: KreFormFieldConfigCache) {
    this.initFieldOptions(field);
    this.getFieldComponentInstance(field).onPopulate();
    if (field.fieldGroup) {
      field.fieldGroup.forEach((f, index) => {
        Object.defineProperty(f, "parent", {
          get: () => field,
          configurable: true,
        });
        Object.defineProperty(f, "index", {
          get: () => index,
          configurable: true,
        });
        this.formId++;
      });
    }
  }

  postPopulate(field: KreFormFieldConfigCache) {
    this.getFieldComponentInstance(field).postPopulate();
  }

  private initFieldOptions(field: KreFormFieldConfigCache) {
    const root = <KreFormFieldConfigCache>field.parent;
    if (!root) {
      return;
    }

    Object.defineProperty(field, "form", {
      get: () => root.formControl,
      configurable: true,
    });
    Object.defineProperty(field, "options", {
      get: () => root.options,
      configurable: true,
    });
    Object.defineProperty(field, "model", {
      get: () =>
        field.key && field.fieldGroup ? getFieldValue(field) : root.model,
      configurable: true,
    });

    reverseDeepMerge(field, {
      id: getFieldId(`formly_${this.formId}`, field, field["index"]),
      hooks: {},
      modelOptions: {},
      templateOptions:
        !field.type || !field.key
          ? {}
          : {
              label: "",
              placeholder: "",
              focus: false,
              disabled: false,
            },
    });

    if (
      this.formlyConfig.extras.resetFieldOnHide &&
      field.resetOnHide !== false
    ) {
      field.resetOnHide = true;
    }

    if (field.lifecycle) {
    }

    if (
      field.type !== "template" &&
      (field.template ||
        (field.expressionProperties && field.expressionProperties.template))
    ) {
      if (field.type) {
      }
      field.type = "template";
    }

    if (!field.type && field.fieldGroup) {
      field.type = "group";
    }

    if (field.type) {
      this.formlyConfig.getMergedField(field);
    }

    if (field.parent) {
      let setDefaultValue =
        !isUndefined(field.key) &&
        !isUndefined(field.defaultValue) &&
        isUndefined(getFieldValue(field)) &&
        (!field.resetOnHide || !(field.hide || field.hideExpression));
      if (setDefaultValue && field.resetOnHide) {
        let parent = field.parent;
        while (parent && !parent.hideExpression && !parent.hide) {
          parent = parent.parent;
        }
        setDefaultValue = !parent || !(parent.hideExpression || parent.hide);
      }

      if (setDefaultValue) {
        assignFieldValue(field, field.defaultValue);
      }
    }

    this.initFieldWrappers(field);
  }

  private initFieldWrappers(field: KreFormFieldConfig) {
    field.wrappers = field.wrappers || [];
    const fieldTemplateManipulators: TemplateManipulators = {
      preWrapper: [],
      postWrapper: [],
      ...(field.templateOptions.templateManipulators || {}),
    };

    field.wrappers = [
      ...this.formlyConfig.templateManipulators.preWrapper.map((m) => m(field)),
      ...fieldTemplateManipulators.preWrapper.map((m) => m(field)),
      ...field.wrappers,
      ...this.formlyConfig.templateManipulators.postWrapper.map((m) =>
        m(field)
      ),
      ...fieldTemplateManipulators.postWrapper.map((m) => m(field)),
    ].filter((el, i, a) => el && i === a.indexOf(el));
  }

  private getFieldComponentInstance(field: KreFormFieldConfigCache) {
    const componentRef = this.formlyConfig.resolveFieldTypeRef(field);
    const instance: KreFormExtension = componentRef
      ? (componentRef.instance as any)
      : {};

    return {
      prePopulate: () => instance.prePopulate && instance.prePopulate(field),
      onPopulate: () => instance.onPopulate && instance.onPopulate(field),
      postPopulate: () => instance.postPopulate && instance.postPopulate(field),
    };
  }
}
